import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { Link } from 'react-router-dom';
import { app } from '../../firebase';
import { toast } from 'react-toastify';
import {
  FaPaperPlane,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEye,
  FaBriefcase,
  FaCalendarAlt,
  FaSearch,
  FaBuilding
} from 'react-icons/fa';

const CandidateInvitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [decliningId, setDecliningId] = useState(null);

  const auth = getAuth(app);
  const db = getDatabase(app);

  useEffect(() => {
    const fetchInvitations = async () => {
      if (!auth.currentUser) return;

      try {
        const invitationsRef = ref(db, `candidate-invitations/${auth.currentUser.uid}`);
        const snapshot = await get(invitationsRef);

        if (snapshot.exists()) {
          const invitationsData = snapshot.val();
          const invitationsArray = Object.keys(invitationsData).map(key => ({
            id: key,
            ...invitationsData[key]
          }));

          // Sort by date sent (most recent first)
          invitationsArray.sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));

          setInvitations(invitationsArray);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching invitations:', error);
        toast.error('Error loading invitations');
        setLoading(false);
      }
    };

    fetchInvitations();
  }, [auth.currentUser, db]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle decline invitation
  const handleDecline = async (invitation) => {
    if (!window.confirm('Are you sure you want to decline this invitation?')) {
      return;
    }

    setDecliningId(invitation.id);

    try {
      const declineData = {
        status: 'declined',
        declinedAt: new Date().toISOString()
      };

      // Step 1: Update candidate's own record (critical path)
      const candidateInvRef = ref(db, `candidate-invitations/${auth.currentUser.uid}/${invitation.id}`);
      await update(candidateInvRef, declineData);

      // Step 2: Update local state and show success immediately
      setInvitations(prev => prev.map(inv =>
        inv.id === invitation.id
          ? { ...inv, ...declineData }
          : inv
      ));
      toast.success('Invitation declined');

      // Step 3: Sync employer record (non-blocking — candidate decline already succeeded)
      // Use the stored employer invitation key to write directly (candidate lacks read access to employer's list)
      try {
        if (invitation.employerInvitationKey) {
          await update(ref(db, `invitations/${invitation.employerId}/${invitation.employerInvitationKey}`), declineData);
        }
      } catch (syncError) {
        console.error('Error syncing decline to employer record:', syncError);
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast.error('Failed to decline invitation');
    } finally {
      setDecliningId(null);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        icon: FaClock,
        text: 'Pending Response',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-600'
      },
      applied: {
        icon: FaCheckCircle,
        text: 'Applied',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        iconColor: 'text-green-600'
      },
      declined: {
        icon: FaTimesCircle,
        text: 'Declined',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        iconColor: 'text-red-600'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon className={`mr-1.5 ${config.iconColor}`} size={14} />
        {config.text}
      </span>
    );
  };

  // Filter invitations
  const filteredInvitations = invitations
    .filter(invitation => {
      // Filter by status
      if (filterStatus !== 'all' && invitation.status !== filterStatus) {
        return false;
      }

      // Filter by search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          invitation.jobTitle?.toLowerCase().includes(searchLower) ||
          invitation.companyName?.toLowerCase().includes(searchLower) ||
          invitation.employerName?.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });

  // Get statistics
  const stats = {
    total: invitations.length,
    pending: invitations.filter(inv => inv.status === 'pending').length,
    applied: invitations.filter(inv => inv.status === 'applied').length,
    declined: invitations.filter(inv => inv.status === 'declined').length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-950"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-blue-950 mb-2">Job Invitations</h1>
        <p className="text-gray-600">Employers who have invited you to apply for their job openings</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Invitations</p>
              <p className="text-2xl font-bold text-blue-950">{stats.total}</p>
            </div>
            <FaPaperPlane className="text-blue-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Awaiting Response</p>
              <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
            </div>
            <FaClock className="text-yellow-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Applied</p>
              <p className="text-2xl font-bold text-green-700">{stats.applied}</p>
            </div>
            <FaCheckCircle className="text-green-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Declined</p>
              <p className="text-2xl font-bold text-red-700">{stats.declined}</p>
            </div>
            <FaTimesCircle className="text-red-500" size={32} />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by job title, company, or employer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div className="md:w-64">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="all">All Status ({stats.total})</option>
              <option value="pending">Awaiting Response ({stats.pending})</option>
              <option value="applied">Applied ({stats.applied})</option>
              <option value="declined">Declined ({stats.declined})</option>
            </select>
          </div>
        </div>
      </div>

      {/* Invitations List */}
      {filteredInvitations.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FaPaperPlane className="mx-auto h-16 w-16 text-gray-300 mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            {invitations.length === 0 ? 'No Invitations Yet' : 'No Results Found'}
          </h3>
          <p className="text-gray-500 mb-6">
            {invitations.length === 0
              ? "You haven't received any job invitations yet. When employers find your profile interesting, they'll invite you to apply!"
              : "No invitations match your search criteria. Try adjusting your filters."}
          </p>
          {invitations.length === 0 && (
            <Link
              to="/candidate/profile"
              className="inline-flex items-center px-6 py-3 bg-blue-950 text-white rounded-md hover:bg-blue-800 transition-colors"
            >
              Complete Your Profile
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInvitations.map((invitation) => (
            <div key={invitation.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* Job Info */}
                <div className="flex-1">
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <FaBriefcase className="text-blue-950" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-blue-950">{invitation.jobTitle}</h3>
                      <div className="flex items-center text-gray-600 mt-1">
                        <FaBuilding className="mr-2" size={14} />
                        {invitation.companyName}
                      </div>
                      <div className="flex items-center text-gray-500 text-sm mt-1">
                        <FaCalendarAlt className="mr-2" size={12} />
                        Invited on {formatDate(invitation.sentAt)}
                      </div>
                      <p className="text-gray-500 text-sm mt-1">
                        Invited by: {invitation.employerName}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="md:text-center">
                  {getStatusBadge(invitation.status)}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {invitation.status === 'pending' && (
                    <>
                      <Link
                        to={`/jobs/${invitation.jobId}`}
                        className="inline-flex items-center px-4 py-2 bg-blue-950 text-white text-sm rounded-md hover:bg-blue-800 transition-colors"
                        title="View Job & Apply"
                      >
                        <FaEye className="mr-2" size={14} />
                        View Job & Apply
                      </Link>
                      <button
                        onClick={() => handleDecline(invitation)}
                        disabled={decliningId === invitation.id}
                        className="inline-flex items-center px-4 py-2 bg-red-100 text-red-700 text-sm rounded-md hover:bg-red-200 transition-colors disabled:opacity-50"
                        title="Decline Invitation"
                      >
                        {decliningId === invitation.id ? (
                          <span className="animate-spin mr-2">⏳</span>
                        ) : (
                          <FaTimesCircle className="mr-2" size={14} />
                        )}
                        Decline
                      </button>
                    </>
                  )}
                  {invitation.status === 'applied' && (
                    <Link
                      to="/candidate/applied"
                      className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 text-sm rounded-md hover:bg-green-200 transition-colors"
                    >
                      <FaCheckCircle className="mr-2" size={14} />
                      View Application
                    </Link>
                  )}
                  {invitation.status === 'declined' && (
                    <span className="text-gray-500 text-sm italic">
                      Declined on {invitation.declinedAt ? formatDate(invitation.declinedAt) : 'N/A'}
                    </span>
                  )}
                </div>
              </div>

              {/* Highlight for pending invitations */}
              {invitation.status === 'pending' && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-800 text-sm">
                    <strong>Tip:</strong> Employers who invite you to apply are already interested in your profile!
                    Responding promptly can improve your chances.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Results Summary */}
      {filteredInvitations.length > 0 && (
        <div className="mt-4 text-sm text-gray-600 text-center">
          Showing {filteredInvitations.length} of {invitations.length} invitation{invitations.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default CandidateInvitations;
